import React, { useState, useEffect, useRef, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Template } from "../api/endpoints/fetchTemplates";
import { useAuth } from "../contexts/AuthContext";
import { fetchMembers, type UserDetailResponse } from "../api/endpoints/users";
import { docToHtml } from "../data/docToHtml";
import docSchema from "../data/doc-schema.json";
import { SkeletonPreview } from "./SkeletonPreview";
import { DocToHtmlPreviewHeader } from "./doctohtml/DocToHtmlPreviewHeader";
import { DocToHtmlErrorBanner } from "./doctohtml/DocToHtmlErrorBanner";
import { DocToHtmlWithContent } from "./doctohtml/DocToHtmlWithContent";
import { useSaveFormMutation } from "./acroformpreview/useSaveFormMutation";
import { useSubmitFormMutation } from "./acroformpreview/useSubmitFormMutation";
import { usePublishFormMutation } from "./acroformpreview/usePublishFormMutation";
import { injectBadgeStyles } from "./acroformpreview/injectBadgeStyles";

interface DocToHtmlFormPreviewProps {
  template: Template;
  onClose: () => void;
  onSaved: (fileId: string) => void;
  onInfo: () => void;
  isInfoPanelOpen: boolean;
}

export function DocToHtmlFormPreview({
  template,
  onClose,
  onSaved,
  onInfo,
  isInfoPanelOpen,
}: DocToHtmlFormPreviewProps) {
  const displayTitle = template.fileTitle || template.fileName;
  const isPublishedForm = template.isPublished;
  const hasMemberId = !!template.memberId;
  const { user, isAdmin } = useAuth();
  const queryClient = useQueryClient();

  // Local state for save feedback
  const [showSuccess, setShowSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);
  const successTimerRef = useRef<number | null>(null);

  // Fetch member details if memberId exists
  const { data: members = [] } = useQuery<UserDetailResponse[]>({
    queryKey: ["memberlist"],
    queryFn: fetchMembers,
    enabled: !!template.memberId,
  });

  // Find member details by memberId
  const memberData = members.find((m) => m.id === template.memberId);
  const memberName = memberData
    ? `${memberData.firstName || ""} ${memberData.lastName || ""}`.trim()
    : "";
  const displayTitleWithMember = memberName
    ? `${displayTitle} - ${memberName}`
    : displayTitle;

  // Only disable save/submit for non-admin users when memberId doesn't exist
  const isSaveDisabled = !isAdmin && !hasMemberId;

  // Track Save Changes intent
  const saveClickedRef = useRef(false);

  // Generate HTML from schema
  const [htmlContent, setHtmlContent] = useState<string>("");

  useEffect(() => {
    try {
      setIsLoading(true);
      const html = docToHtml(docSchema);
      setHtmlContent(html);
      setIsError(false);
      setError(null);
    } catch (err) {
      setIsError(true);
      setError(err instanceof Error ? err : new Error("Failed to generate HTML"));
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Inject minimal CSS for success/error badges
  useEffect(() => {
    injectBadgeStyles();
  }, []);

  // Save form mutation
  const { mutate, isPending: mutationIsPending } = useSaveFormMutation({
    template,
    user: user ?? null,
    successTimerRef,
    setShowSuccess,
    setErrorText,
    onSaved,
    onClose,
  });

  useEffect(() => {
    return () => {
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
      }
    };
  }, []);

  // Submit form mutation
  const submitFormMutation = useSubmitFormMutation({
    template,
    user: user ?? null,
    email: user?.email || "",
    successTimerRef,
    setShowSuccess,
    setErrorText,
    onSaved,
    onClose,
  });

  // Publish form mutation
  const publishFormMutation = usePublishFormMutation({
    template,
    email: user?.email || "",
    successTimerRef,
    setShowSuccess,
    setErrorText,
    onSaved,
    onClose,
  });

  // Collect form values from iframe
  const collectFormValues = useCallback(() => {
    const iframeElement = document.querySelector(
      ".doc-preview-form iframe"
    ) as HTMLIFrameElement;
    if (!iframeElement?.contentDocument) return {};

    const container = iframeElement.contentDocument.querySelector(".form-content");
    if (!container) return {};

    const values: Record<string, string | boolean> = {};
    const inputs = container.querySelectorAll("input, textarea, select");

    inputs.forEach((el) => {
      const dataId = (el as HTMLElement).getAttribute("data-id");
      if (!dataId) return;

      if (el instanceof HTMLInputElement) {
        if (el.type === "checkbox" || el.type === "radio") {
          values[dataId] = el.checked;
        } else {
          values[dataId] = el.value;
        }
      } else if (el instanceof HTMLTextAreaElement) {
        values[dataId] = el.value;
      } else if (el instanceof HTMLSelectElement) {
        values[dataId] = el.value;
      }
    });

    return values;
  }, []);

  // Handle Save Changes
  const handleSaveChanges = useCallback(() => {
    saveClickedRef.current = true;
    const values = collectFormValues();
    mutate({
      fileId: template.fileId,
      data: values,
      formStatus: "draft",
    });
  }, [collectFormValues, mutate, template.fileId]);

  // Handle Submit Form
  const handleSubmitForm = useCallback(() => {
    const values = collectFormValues();
    submitFormMutation.mutate({
      fileId: template.fileId,
      data: values,
      formStatus: "submitted",
    });
  }, [collectFormValues, submitFormMutation, template.fileId]);

  // Handle Publish Form
  const handlePublishForm = useCallback(() => {
    publishFormMutation.mutate({
      fileId: template.fileId,
    });
  }, [publishFormMutation, template.fileId]);

  return (
    <div
      className={`doc-preview-form fixed inset-0 bg-white z-50 flex flex-col transition-all duration-300 ${
        isInfoPanelOpen ? "backdrop-blur-sm bg-white/95" : ""
      }`}
    >
      {/* Preview Header */}
      <DocToHtmlPreviewHeader
        displayTitle={displayTitleWithMember}
        onInfo={onInfo}
        onClose={onClose}
        isInfoPanelOpen={isInfoPanelOpen}
      />

      <div
        className={`flex flex-col flex-auto overflow-hidden transition-all duration-300 ${
          isInfoPanelOpen ? "opacity-75" : ""
        }`}
      >
        {isLoading && <SkeletonPreview />}

        {/* Error */}
        {isError && (
          <DocToHtmlErrorBanner
            message={error instanceof Error ? error.message : undefined}
          />
        )}

        {/* Form Content */}
        {!isLoading && !isError && (
          <DocToHtmlWithContent
            htmlContent={htmlContent}
            isPublishedForm={isPublishedForm}
            errorText={errorText}
            showSuccess={showSuccess}
            mutationIsPending={mutationIsPending}
            submitFormPending={submitFormMutation.isPending}
            publishFormPending={publishFormMutation.isPending}
            isSaveDisabled={isSaveDisabled}
            isSubmitEnabled={isAdmin || hasMemberId}
            onSaveChanges={handleSaveChanges}
            onSubmitForm={handleSubmitForm}
            onPublishForm={handlePublishForm}
          />
        )}
      </div>
    </div>
  );
}

export default DocToHtmlFormPreview;
